import { walletAmountFeedback, walletResultMessage, WALLET_UNKNOWN_OUTCOME } from '@/features/usdtDeposit/helpers/walletFeedback.helper';
import { getAuthSessionRevision } from '@/lib/authStorage';
import { UsdtWithdrawal } from '@/features/usdtDeposit/components/UsdtWithdrawal.component';
import { getDepositMode, getManualDepositPolicy, getUsdtWithdrawalPolicy, isUsdtHistoryRow, walletOwnerKey } from '@/features/usdtDeposit/helpers/depositMode.helper';
import { withoutBankInquiryActions } from '@/features/usdtDeposit/helpers/depositGuide.helper';
import { usePersonalDepositWallet } from '@/features/usdtDeposit/hooks/usePersonalDepositWallet.hook';
import { useWalletRequestScope } from '@/features/usdtDeposit/hooks/useWalletRequestScope.hook';
import { ManualUsdtDeposit } from '@/features/usdtDeposit/components/ManualUsdtDeposit.component';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gameApi, siteApi } from "@/lib/api";
import { normalizeBackendRichText } from "@/utils/backendRichText";
import { UsdtDepositPanel } from "./CryptoDepositSections";
import WalletHistorySection from "./WalletHistorySection";
import WalletNoticeModal from "./WalletNoticeModal";
import useWalletHistoryDelete from "./hooks/useWalletHistoryDelete";
import {
  formatAmountInput,
  formatMoney,
  formatPoint,
  formatUsdt,
  getBonusId,
  getBonusTitle,
  getHistoryAmount,
  getMoneyTypeTabs,
  getProfileAccountNumber,
  getProfileBalance,
  getProfileDepositorName,
  getProfileUsdtAddress,
  getResponseRows,
  getResponseTotal,
  getResultMessage,
  getSelectedBank,
  getUsdtRate,
  getWalletNoticeHtml,
  HISTORY_LIMIT,
  isAnonymousProfile,
  isSuccessResult,
  MONEY_TYPES,
  maskAccountNumber,
  QUICK_AMOUNTS,
  toNumber,
  WALLET_VIEWS,
} from "./financeWallet.helpers";

const VIEW_CONFIG = {
  [WALLET_VIEWS.DEPOSIT]: {
    title: "입금",
    description: "입금계좌 문의 후 금액을 신청해주세요.",
    balanceType: "money",
    balanceLabel: "보유금",
    unit: "원",
    submitLabel: "입금 신청",
    historyType: "CREDIT",
  },
  [WALLET_VIEWS.WITHDRAW]: {
    title: "출금",
    description: "출금 가능 금액과 비밀번호를 확인해주세요.",
    balanceType: "money",
    balanceLabel: "출금 가능",
    unit: "원",
    submitLabel: "출금 신청",
    historyType: "DEBIT",
  },
  [WALLET_VIEWS.POINT]: {
    title: "포인트",
    description: "보유 포인트를 게임머니로 전환합니다.",
    balanceType: "point",
    balanceLabel: "보유 포인트",
    unit: "P",
    submitLabel: "포인트 전환",
  },
  [WALLET_VIEWS.COMP]: {
    title: "콤프",
    description: "보유 콤프를 게임머니로 전환합니다.",
    balanceType: "comp",
    balanceLabel: "보유 콤프",
    unit: "P",
    submitLabel: "콤프 전환",
  },
};

const normalizeAmountInput = (value) => String(value || "").replace(/,/g, "");
const HISTORY_MIN_ROWS = 4;

const WalletPanel = ({ view, profile, settings, authenticated = false, onBack, onRefreshWallet }) => {
  const config = VIEW_CONFIG[view] || VIEW_CONFIG[WALLET_VIEWS.DEPOSIT];
  const isFinanceView = view === WALLET_VIEWS.DEPOSIT || view === WALLET_VIEWS.WITHDRAW;
  const [moneyType, setMoneyType] = useState(MONEY_TYPES.GENERAL);
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [selectedBonusId, setSelectedBonusId] = useState("");
  const [bonusOptions, setBonusOptions] = useState([]);
  const [bankRows, setBankRows] = useState([]);
  const [loadedHistoryRows, setHistoryRows] = useState([]);
  const [historyScope, setHistoryScope] = useState(null);
  const [contentReady, setContentReady] = useState(false);
  const submitRequestRef = useRef(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountInquirySubmitting, setAccountInquirySubmitting] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const contentRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const isAnonymous = isAnonymousProfile(profile);
  const userUsdtAddress = getProfileUsdtAddress(profile);
  const profileScopeKey = walletOwnerKey(profile);
  const depositMode = getDepositMode(settings);
  const manualPolicy = getManualDepositPolicy(settings);
  const withdrawalPolicy = getUsdtWithdrawalPolicy(settings, userUsdtAddress);
  const selectedBank = useMemo(() => getSelectedBank(bankRows, profile), [bankRows, profile]);
  const moneyTypeTabs = useMemo(
    () =>
      isFinanceView
        ? getMoneyTypeTabs(settings, { includeUsdt: true, withdrawal: view === WALLET_VIEWS.WITHDRAW })
        : [],
    [isFinanceView, settings, view]
  );
  const activeMoneyType = moneyTypeTabs.some((tab) => tab.key === moneyType)
    ? moneyType
    : moneyTypeTabs[0]?.key || MONEY_TYPES.GENERAL;
  const isUsdtMode = isFinanceView && activeMoneyType === MONEY_TYPES.USDT;
  const isUsdtDepositMode = view === WALLET_VIEWS.DEPOSIT && isUsdtMode;
  const isUsdtWithdrawal = view === WALLET_VIEWS.WITHDRAW && isUsdtMode;
  const isHoldemMode = isFinanceView && activeMoneyType === MONEY_TYPES.HOLDEM;
  const isAutomaticUsdtDeposit = authenticated && isUsdtDepositMode && depositMode === 'automatic';
  const isManualUsdtDeposit = isUsdtDepositMode && depositMode === 'manual';
  const scope = JSON.stringify([authenticated, getAuthSessionRevision(), profileScopeKey, profile?.signup_type, view, activeMoneyType, depositMode, manualPolicy.address, String(manualPolicy.rate), settings?.crypto_withdrawal_enabled, withdrawalPolicy.address]);
  const currentScope = useWalletRequestScope(scope);
  const isCurrentScope = useCallback(() => authenticated && currentScope(), [authenticated, currentScope]);
  const personalWallet = usePersonalDepositWallet({ active: isAutomaticUsdtDeposit, scope });
  const { wallet: cryptoWallet, rate: cryptoRate, loading: cryptoLoading, error: cryptoWalletError, retry: retryCryptoWallet } = personalWallet;
  const activeUsdtRate = isUsdtWithdrawal ? withdrawalPolicy.rate : isManualUsdtDeposit ? manualPolicy.rate : getUsdtRate(settings, cryptoRate);
  const historyRows = historyScope === scope ? loadedHistoryRows : [];
  const requestMoneyType = isUsdtMode ? MONEY_TYPES.GENERAL : activeMoneyType;
  const requestChannel = isUsdtMode ? 'usdt' : 'bank';


  const activeBalanceType = useMemo(() => {
    if (!isFinanceView) return config.balanceType;
    return isHoldemMode ? "holdemMoney" : "money";
  }, [config.balanceType, isFinanceView, isHoldemMode]);

  const activeBalanceLabel = useMemo(() => {
    if (!isFinanceView) return config.balanceLabel;
    if (isHoldemMode) return view === WALLET_VIEWS.WITHDRAW ? "홀덤 출금 가능" : "홀덤머니";
    if (isUsdtMode) return view === WALLET_VIEWS.WITHDRAW ? "USDT 출금 기준" : "USDT 입금 기준";
    return config.balanceLabel;
  }, [config.balanceLabel, isFinanceView, isHoldemMode, isUsdtMode, view]);

  const activeDescription = useMemo(() => {
    if (isUsdtDepositMode) return "TRC-20 입금 주소를 확인하고 전송해주세요.";
    if (isUsdtMode && view === WALLET_VIEWS.WITHDRAW) {
      return "등록된 USDT 주소와 환산 금액을 확인해주세요.";
    }
    if (isHoldemMode) return view === WALLET_VIEWS.WITHDRAW ? "홀덤머니 출금을 신청합니다." : "홀덤머니 입금을 신청합니다.";
    return config.description;
  }, [config.description, isHoldemMode, isUsdtDepositMode, isUsdtMode, view]);

  const balance = useMemo(() => getProfileBalance(profile, activeBalanceType), [profile, activeBalanceType]);
  const balanceText = config.unit === "원" ? `${formatMoney(balance)} 원` : formatPoint(balance);
  const guideHtml = useMemo(
    () => {
      const html = normalizeBackendRichText(getWalletNoticeHtml(settings, profile, view));
      return isUsdtMode ? withoutBankInquiryActions(html) : html;
    },
    [isUsdtMode, profile, settings, view]
  );
  const historyCount = historyScope === scope ? historyTotal || historyRows.length : 0;
  const totalPages = Math.max(1, Math.ceil(historyCount / HISTORY_LIMIT));
  const historySkeletonRows = Math.max(HISTORY_MIN_ROWS, Math.min(HISTORY_LIMIT, historyRows.length || 0));

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message: walletResultMessage({ message }, type === "success" ? "신청이 완료되었습니다." : "요청 처리 중 문제가 발생했습니다.") });
  }, []);

  const loadContent = useCallback(
    async ({ silent = false, pageOverride } = {}) => {
      if (!isCurrentScope()) return null;
      const requestId = contentRequestIdRef.current + 1;
      contentRequestIdRef.current = requestId;
      const isCurrentRequest = () => isCurrentScope() && contentRequestIdRef.current === requestId;
      setContentReady(false);
      if (!silent) setIsLoading(true);
      const targetPage = pageOverride || historyPage;

      try {
        if (isFinanceView) {
          if (isAutomaticUsdtDeposit) {
            setBonusOptions([]);
            const depositResult = await gameApi.finance
              .getCryptoDeposits({ page: targetPage, limit: HISTORY_LIMIT }, isCurrentRequest).catch(() => null);
            if (!isCurrentRequest()) return null;
            const rows =
              depositResult && isSuccessResult(depositResult)
                ? getResponseRows(depositResult.data)
                : [];
            const total =
              depositResult && isSuccessResult(depositResult)
                ? getResponseTotal(depositResult.data, rows)
                : 0;
            setHistoryRows(rows);
            setHistoryScope(scope);
            setHistoryTotal(total);
            return {
              rows,
              total,
              success: Boolean(depositResult && isSuccessResult(depositResult)),
            };
          }

          const bonusParams = { money_type: requestMoneyType };
          if (profile?.level !== undefined && profile?.level !== null) {
            bonusParams.level = profile.level;
          }
          const shouldLoadBonus = view === WALLET_VIEWS.DEPOSIT;
          if (shouldLoadBonus) setBonusLoading(true);
          const [listResult, bonusResult] = await Promise.all([
            gameApi.finance.getDepositWithdrawalList({
              type: config.historyType,
              page: targetPage,
              limit: HISTORY_LIMIT,
              ...(requestChannel === 'usdt' ? {} : { money_type: requestMoneyType }),
              request_channel: requestChannel,
            }, isCurrentRequest),
            shouldLoadBonus
              ? gameApi.finance.getBonusList(bonusParams, isCurrentRequest)
              : Promise.resolve(null),
          ]);
          if (!isCurrentRequest()) return null;

          const listSucceeded = isSuccessResult(listResult);
          const rows = listSucceeded ? getResponseRows(listResult.data) : [];
          const total = listSucceeded
            ? getResponseTotal(listResult.data, rows)
            : 0;
          setHistoryRows(rows);
        setHistoryScope(scope);
          setHistoryTotal(total);

          setContentReady(listSucceeded && (!shouldLoadBonus || isSuccessResult(bonusResult)));
          if (shouldLoadBonus) {
            setBonusOptions(isSuccessResult(bonusResult) ? getResponseRows(bonusResult.data) : []);
          } else {
            setBonusOptions([]);
          }
          return { rows, total, success: listSucceeded };
        }

        const result =
          view === WALLET_VIEWS.POINT
            ? await gameApi.finance.getPointHistory({ page: targetPage, limit: HISTORY_LIMIT }, isCurrentRequest)
            : await gameApi.finance.getCompHistory({
                page: targetPage,
                limit: HISTORY_LIMIT,
                money_type: MONEY_TYPES.GENERAL,
              }, isCurrentRequest);
        if (!isCurrentRequest()) return null;

        const historySucceeded = isSuccessResult(result);
        const rows = historySucceeded ? getResponseRows(result.data) : [];
        const total = historySucceeded ? getResponseTotal(result.data, rows) : 0;
        setHistoryRows(rows);
        setHistoryScope(scope);
        setHistoryTotal(total);
        setBonusOptions([]);
        return { rows, total, success: historySucceeded };
      } catch {
        if (isCurrentRequest()) { setHistoryRows([]); setHistoryTotal(0); setBonusOptions([]); }
        return null;
      } finally {
        if (isCurrentRequest()) {
          if (!silent) setIsLoading(false);
          setBonusLoading(false);
        }
      }
    },
    [
      isCurrentScope,
      scope,
      requestMoneyType,
      requestChannel,
      config.historyType,
      historyPage,
      isFinanceView,
      isAutomaticUsdtDeposit,
      profile?.level,
      view,
    ]
  );

  const {
    canDeleteHistory,
    closeNotice,
    confirmDelete: confirmHistoryDelete,
    getDeleteId: getHistoryDeleteId,
    isDeletePending: isHistoryDeletePending,
    requestDelete: requestHistoryDelete,
  } = useWalletHistoryDelete({
    isCurrentScope,
    historyPage,
    isUsdtDepositMode: isAutomaticUsdtDeposit,
    loadContent,
    notice,
    setHistoryPage,
    setNotice,
    showNotice,
    view,
  });

  useEffect(() => {
    setMoneyType(MONEY_TYPES.GENERAL);
    setAmount("");
    setPassword("");
    setAccountPassword("");
    setSelectedBonusId("");
    setHistoryPage(1);
    setIsGuideOpen(false);
    setNotice(null);
  }, [view]);

  useEffect(() => {
    setHistoryPage(1);
  }, [profileScopeKey]);

  useEffect(() => {
    if (!isFinanceView || !moneyTypeTabs.length) return;
    if (!moneyTypeTabs.some((tab) => tab.key === moneyType)) {
      setMoneyType(moneyTypeTabs[0].key);
    }
  }, [isFinanceView, moneyType, moneyTypeTabs]);

  useEffect(() => {
    setAmount("");
    setPassword("");
    setAccountPassword("");
    setSelectedBonusId("");
    setHistoryPage(1);
    setNotice(null);
    submitRequestRef.current = null;
    setIsSubmitting(false);
    setAccountInquirySubmitting(false);
  }, [scope]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      contentRequestIdRef.current += 1;
      };
  }, []);

  useEffect(() => {
    if (!authenticated || !isFinanceView) return undefined;
    let mounted = true;
    setBankLoading(true);
    siteApi
      .getBanks({ limit: "ALL" })
      .then((result) => {
        if (!mounted) return;
        setBankRows(isSuccessResult(result) ? getResponseRows(result.data) : []);
      })
      .finally(() => {
        if (mounted) setBankLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [authenticated, isFinanceView]);

  useEffect(() => {
    loadContent();
    return () => {
      contentRequestIdRef.current += 1;
    };
  }, [loadContent, profileScopeKey]);

  const updateAmount = (value) => {
    setAmount(normalizeAmountInput(value));
  };

  const addQuickAmount = (value) => {
    setAmount(String(toNumber(amount) + toNumber(value)));
  };

  const resetAmount = () => {
    setAmount("");
  };

  const copyToClipboard = async (value) => {
    if (!isCurrentScope()) return;
    if (!value) {
      showNotice("error", "복사할 주소가 없습니다.");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      if (!isCurrentScope()) return;
      showNotice("success", "주소가 복사되었습니다.");
    } catch {
      if (isCurrentScope()) showNotice("error", "주소 복사에 실패했습니다.");
    }
  };

  const handleAccountInquiry = async () => {
    if (isUsdtDepositMode || !isCurrentScope() || accountInquirySubmitting) return;
    if (settings?.enter_exchange_password_when_account_inquiry && !accountPassword.trim()) {
      showNotice("error", "계좌문의 비밀번호를 입력해주세요.");
      return;
    }

    setAccountInquirySubmitting(true);
    try {
      const payload = settings?.enter_exchange_password_when_account_inquiry
        ? { password: accountPassword }
        : {};
      const result = await gameApi.finance.requestAccountInquiry(payload);
      if (!isCurrentScope()) return;
      showNotice(
        isSuccessResult(result) ? "success" : "error",
        getResultMessage(
          result,
          isSuccessResult(result) ? "입금계좌 문의가 접수되었습니다." : "입금계좌 문의에 실패했습니다."
        )
      );
      if (isSuccessResult(result)) setAccountPassword("");
    } catch (error) {
      if (isCurrentScope()) showNotice('error', error?.message || '입금계좌 문의에 실패했습니다.');
    } finally {
      if (isCurrentScope()) setAccountInquirySubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitRequestRef.current || !isCurrentScope()) return;

    if (isAutomaticUsdtDeposit) {
      showNotice("success", "표시된 TRC-20 주소로 입금하면 확인 후 자동 반영됩니다.");
      return;
    }

    if (isUsdtDepositMode && (!isManualUsdtDeposit || !manualPolicy.ready)) { showNotice('error', manualPolicy.error || 'USDT 입금 정보를 확인해주세요.'); return; }
    if (isUsdtDepositMode && (!contentReady || historyScope !== scope)) { showNotice('error', '입금 정보를 불러오지 못했습니다. 잠시 후 다시 확인해주세요.'); return; }
    if (isUsdtWithdrawal && !withdrawalPolicy.ready) { showNotice('error', withdrawalPolicy.error); return; }
    const inputError = walletAmountFeedback({ amount, balance, withdrawal: view === WALLET_VIEWS.WITHDRAW, password });
    if (inputError) { showNotice('error', inputError); return; }
    const numericAmount = toNumber(amount);
    if (!numericAmount || numericAmount <= 0) {
      showNotice("error", "금액을 입력해주세요.");
      return;
    }

    if (view === WALLET_VIEWS.WITHDRAW && !password.trim()) {
      showNotice("error", "출금 비밀번호를 입력해주세요.");
      return;
    }

    if (view === WALLET_VIEWS.DEPOSIT) {
      if (bonusLoading) {
        showNotice("error", "보너스 목록을 불러오는 중입니다.");
        return;
      }
      if (bonusOptions.length > 0 && !selectedBonusId) {
        showNotice("error", "입금 보너스를 선택해주세요.");
        return;
      }
    }

    const submission = {};
    submitRequestRef.current = submission;
    setIsSubmitting(true);
    try {
      let result;
      if (view === WALLET_VIEWS.DEPOSIT) {
        const payload = { amount: numericAmount, request_channel: requestChannel };
        if (isUsdtMode || requestMoneyType !== MONEY_TYPES.GENERAL) payload.money_type = requestMoneyType;
        if (selectedBonusId) payload.bonus_id = selectedBonusId;
        result = await gameApi.finance.requestDeposit(payload, isCurrentScope);
      } else if (view === WALLET_VIEWS.WITHDRAW) {
        const payload = {
          amount: numericAmount,
          password,
          request_channel: requestChannel,
        };
        if (isUsdtWithdrawal) payload.money_type = MONEY_TYPES.GENERAL;
        if (activeMoneyType === MONEY_TYPES.HOLDEM) {
          payload.money_type = MONEY_TYPES.HOLDEM;
        }
        result = await gameApi.finance.requestWithdrawal(payload, isCurrentScope);
      } else if (view === WALLET_VIEWS.POINT) {
        result = await gameApi.finance.convertPoint({ amount: numericAmount });
      } else {
        result = await gameApi.finance.convertComp({ amount: numericAmount });
      }

      if (!isCurrentScope()) return;
      const succeeded = isSuccessResult(result);
      showNotice(
        succeeded ? "success" : "error",
        getResultMessage(result, succeeded ? "신청이 완료되었습니다." : WALLET_UNKNOWN_OUTCOME)
      );

      if (succeeded) {
        setAmount("");
        setPassword("");
        setSelectedBonusId("");
        setHistoryPage(1);
        await loadContent({ silent: true, pageOverride: 1 });
        if (isCurrentScope() && onRefreshWallet) await onRefreshWallet();
      }
    } catch (error) {
      if (isCurrentScope()) showNotice('error', walletResultMessage(error, WALLET_UNKNOWN_OUTCOME));
    } finally {
      if (submitRequestRef.current === submission) submitRequestRef.current = null;
      if (isCurrentScope()) setIsSubmitting(false);
    }
  };

  const renderMoneyTabs = () =>
    moneyTypeTabs.length > 1 ? (
      <div className="wallet-money-tabs" role="tablist" aria-label="입출금 종류">
        {moneyTypeTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeMoneyType === tab.key ? "is-active" : ""}
            onClick={() => setMoneyType(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    ) : null;

  const renderInfoTiles = () => {
    if (!isFinanceView || isUsdtMode) return null;

    const accountNumber = getProfileAccountNumber(profile);
    const depositorName = getProfileDepositorName(profile);
    const tiles = [];

    if (isAnonymous) {
      tiles.push({
        label: "내 USDT 주소",
        value: userUsdtAddress || "등록되지 않음",
      });
    } else {
      tiles.push(
        {
          label: "은행",
          value: bankLoading ? "확인 중" : selectedBank?.bank_name || "-",
        },
        {
          label: "예금주",
          value: depositorName || "-",
        },
        {
          label: "계좌번호",
          value: maskAccountNumber(accountNumber),
        }
      );
      if (isUsdtMode) {
        tiles.push({
          label: "내 USDT 주소",
          value: userUsdtAddress || "등록되지 않음",
        });
      }
    }

    tiles.push({
      label: isHoldemMode ? "홀덤머니" : "보유머니",
      value: `${formatMoney(balance)} 원`,
    });

    if (isUsdtMode && activeUsdtRate > 0) {
      tiles.push({
        label: "USDT 환율",
        value: `1 USDT = ${formatMoney(activeUsdtRate)} 원`,
      });
    }

    return (
      <div className="wallet-info-grid">
        {tiles.map((tile) => (
          <div className="wallet-info-tile" key={`${tile.label}-${tile.value}`}>
            <span>{tile.label}</span>
            <strong>{tile.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  const renderAmountField = (label = "금액") => (
    <div className="wallet-field">
      <label htmlFor={`wallet-amount-${view}-${activeMoneyType}`}>{label}</label>
      <div className="wallet-input-shell">
        <input
          id={`wallet-amount-${view}-${activeMoneyType}`}
          type="text"
          inputMode="numeric"
          value={/^\d*$/.test(amount) ? formatAmountInput(amount) : amount}
          onChange={(event) => updateAmount(event.target.value)}
          placeholder="금액 입력"
        />
        <span>{config.unit}</span>
      </div>
      <div className="wallet-quick-amounts">
        {QUICK_AMOUNTS.map((quickAmount) => (
          <button type="button" key={quickAmount} onClick={() => addQuickAmount(quickAmount)}>
            {formatMoney(quickAmount)}
          </button>
        ))}
        {!isFinanceView ? (
          <button type="button" className="is-max" onClick={() => setAmount(normalizeAmountInput(balance))}>
            전액
          </button>
        ) : null}
        <button type="button" className="is-reset" onClick={resetAmount}>
          정정
        </button>
      </div>
    </div>
  );

  const renderDepositFields = () => (
    <>
      {!isUsdtDepositMode ? <div className="wallet-inline-actions">
        <button
          type="button"
          className="wallet-secondary-button"
          onClick={handleAccountInquiry}
          disabled={accountInquirySubmitting}
        >
          {accountInquirySubmitting ? "문의 중" : "입금계좌 문의"}
        </button>
        <p>계좌 정보가 필요한 경우 먼저 문의를 접수해주세요.</p>
      </div> : null}

      {!isUsdtDepositMode && settings?.enter_exchange_password_when_account_inquiry ? (
        <div className="wallet-field">
          <label htmlFor="wallet-account-password">계좌문의 비밀번호</label>
          <div className="wallet-input-shell">
            <input
              id="wallet-account-password"
              type="password"
              value={accountPassword}
              onChange={(event) => setAccountPassword(event.target.value)}
              placeholder="출금 비밀번호"
            />
          </div>
        </div>
      ) : null}

      {bonusOptions.length > 0 ? (
        <div className="wallet-field">
          <label htmlFor="wallet-bonus">입금 보너스</label>
          <select
            id="wallet-bonus"
            value={selectedBonusId}
            onChange={(event) => setSelectedBonusId(event.target.value)}
            disabled={bonusLoading}
          >
            <option value="">{bonusLoading ? "보너스 확인 중" : "보너스 선택"}</option>
            {bonusOptions.map((bonus, index) => {
              const bonusId = getBonusId(bonus);
              return (
                <option key={bonusId || `${getBonusTitle(bonus)}-${index}`} value={bonusId || ""}>
                  {getBonusTitle(bonus)}
                </option>
              );
            })}
          </select>
        </div>
      ) : null}

    </>
  );

  const renderWithdrawFields = () => (
    <>
      <div className="wallet-field">
        <label htmlFor="wallet-withdraw-password">출금 비밀번호</label>
        <div className="wallet-input-shell">
          <input
            id="wallet-withdraw-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="출금 비밀번호"
          />
        </div>
      </div>

    </>
  );

  const historyUnit = isAutomaticUsdtDeposit ? "USDT" : config.unit === "원" ? "원" : "P";
  const historyTitle =
    view === WALLET_VIEWS.DEPOSIT
      ? isAutomaticUsdtDeposit
        ? "USDT 입금 내역"
        : isManualUsdtDeposit ? "USDT 입금 신청 내역" : "입금 신청 내역"
      : view === WALLET_VIEWS.WITHDRAW
        ? isUsdtWithdrawal ? "USDT 출금 신청 내역" : "출금 신청 내역"
        : "최근 내역";

  const renderHistoryAmount = (row) => {
    const historyAmount = getHistoryAmount(row);
    if (historyUnit === "USDT") return `${formatUsdt(historyAmount)} USDT`;
    return `${formatMoney(historyAmount)} ${historyUnit}`;
  };

  const renderHistoryLabel = (row) => {
    if (isAutomaticUsdtDeposit || isUsdtHistoryRow(row)) return "USDT";
    if (row?.money_type === MONEY_TYPES.HOLDEM) return "홀덤";
    return config.title;
  };

  if (!authenticated) return <p role="status">로그인 후 입출금 정보를 확인해주세요.</p>;

  return (
    <div className="cashier-detail">
      <button type="button" className="wallet-back-button" onClick={onBack}>
        <span aria-hidden="true">‹</span>
        {config.title}
      </button>

      {renderMoneyTabs()}

      <section className="wallet-summary-card">
        <div>
          <span>{activeBalanceLabel}</span>
          <strong>{balanceText}</strong>
        </div>
        <p>{activeDescription}</p>
      </section>

      {renderInfoTiles()}

      {guideHtml ? (
        <section className={`wallet-guide ${isGuideOpen ? "is-open" : ""}`}>
          <button type="button" className="wallet-guide-toggle" onClick={() => setIsGuideOpen((open) => !open)}>
            <span>안내사항</span>
            <strong>{isGuideOpen ? "접기" : "안내보기"}</strong>
          </button>
          {isGuideOpen ? (
            <div className="wallet-guide-content" dangerouslySetInnerHTML={{ __html: guideHtml }} />
          ) : null}
        </section>
      ) : null}

      <form className="wallet-form" onSubmit={handleSubmit}>
        {!isAutomaticUsdtDeposit ? renderAmountField(view === WALLET_VIEWS.WITHDRAW ? "출금 금액" : "금액") : null}
        {view === WALLET_VIEWS.DEPOSIT ? (
          isAutomaticUsdtDeposit ? (
            <UsdtDepositPanel
              activeUsdtRate={activeUsdtRate}
              cryptoLoading={cryptoLoading}
              cryptoWallet={cryptoWallet}
              cryptoWalletError={cryptoWalletError}
              onCopyAddress={copyToClipboard}
              onRetryWallet={retryCryptoWallet}
            />
          ) : (
            <>
              {isManualUsdtDeposit ? <ManualUsdtDeposit policy={manualPolicy} amount={amount} onCopyAddress={copyToClipboard} /> : null}
              {renderDepositFields()}
            </>
          )
        ) : null}
        {view === WALLET_VIEWS.WITHDRAW ? <>
          {isUsdtWithdrawal ? <UsdtWithdrawal policy={withdrawalPolicy} amount={amount} /> : null}
          {renderWithdrawFields()}
        </> : null}

        {!isAutomaticUsdtDeposit ? (
          <button type="submit" className="wallet-submit-button" disabled={isSubmitting}>
            {isSubmitting ? "처리 중" : config.submitLabel}
          </button>
        ) : null}
      </form>

      <WalletHistorySection
        canDeleteHistory={canDeleteHistory}
        canDeleteAll={!isFinanceView}
        currentPage={historyPage}
        getDeleteId={getHistoryDeleteId}
        historyCount={historyCount}
        historyRows={historyRows}
        historySkeletonRows={historySkeletonRows}
        historyTitle={historyTitle}
        isCryptoHistory={isAutomaticUsdtDeposit}
        isDeletePending={isHistoryDeletePending}
        isLoading={isLoading}
        onDeleteAll={() => requestHistoryDelete("all")}
        onDeleteRow={(_, deleteId) => requestHistoryDelete("row", deleteId)}
        onNextPage={() => setHistoryPage((page) => Math.min(totalPages, page + 1))}
        onPreviousPage={() => setHistoryPage((page) => Math.max(1, page - 1))}
        renderAmount={renderHistoryAmount}
        renderLabel={renderHistoryLabel}
        totalPages={totalPages}
      />

      <WalletNoticeModal
        notice={notice}
        onClose={closeNotice}
        onConfirm={confirmHistoryDelete}
        isSubmitting={isHistoryDeletePending}
      />
    </div>
  );
};

export default WalletPanel;
